import type { TypedDocumentNode } from '@apollo/client';
import { gql, useApolloClient } from '@apollo/client';
import { useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import {
  ManagerProgressionTaskSlug,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { CommonMissionsTutorialDialog } from '#src/components/missions/CommonMissionsTutorialDialog/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';
import { getTasksSections } from '#src/components/missions/getTasksSections.tsx';
import { DecisivePlayerPickerDialog } from '#src/components/missions/picker/DecisivePlayerPickerDialog/index.tsx';
import { MissionPageSkeleton } from '#src/pages/MissionsPage/MissionPageSkeleton/index.tsx';
import { MissionsPage } from '#src/pages/MissionsPage/index.tsx';

import type {
  MissionsCategoryRarityPageQuery,
  MissionsCategoryRarityPageQueryVariables,
  RefreshDailyActionTaskQuery,
  RefreshDailyActionTaskQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const MISSIONS_CATEGORY_RARITY_PAGE_QUERY = gql`
  query MissionsCategoryRarityPageQuery(
    $taskGroupSlug: String!
    $sport: Sport!
    $rarity: Rarity!
  ) {
    currentUser {
      slug
      id
      taskGroup(slug: $taskGroupSlug) {
        slug
        myTasks(sport: $sport, rarity: $rarity) {
          id
          ...TaskClaimedDialogs_taskInterface
        }
        ...getTasksSections_taskGroup
      }
    }
  }
  ${getTasksSections.fragments.taskGroup}
  ${TaskClaimedDialogs.fragments.taskInterface}
` as TypedDocumentNode<
  MissionsCategoryRarityPageQuery,
  MissionsCategoryRarityPageQueryVariables
>;

const REFRESH_DAILY_ACTION_TASK_QUERY = gql`
  query RefreshDailyActionTaskQuery($id: ID!) {
    currentUser {
      slug
      task(id: $id) {
        id
        ...getTasksSections_dailyActionTaskInterface
      }
    }
  }
  ${getTasksSections.fragments.dailyActionTaskInterface}
` as TypedDocumentNode<
  RefreshDailyActionTaskQuery,
  RefreshDailyActionTaskQueryVariables
>;

export const loader = queryLoader<PageParams>(
  MISSIONS_CATEGORY_RARITY_PAGE_QUERY,
  ({ category, sport, rarity }) => ({
    taskGroupSlug: category,
    sport,
    rarity,
  })
);

export const Fallback = MissionPageSkeleton;

const MissionsCategoryRarityPage = () => {
  const { category, rarity } = useTypedParams<PageParams>();
  const isLaptop = useIsLaptop();
  const { sport } = useSportContext();
  const { data } = useQueryLoader<MissionsCategoryRarityPageQuery>();
  const [showDecisivePlayerPickerDialog, setShowDecisivePlayerPickerDialog] =
    useState(false);
  const dailyActionTaskId = data?.currentUser?.taskGroup?.myTasks.find(
    task => task.name === ManagerProgressionTaskSlug.DAILY_ACTION
  )?.id;
  const apolloClient = useApolloClient();

  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  const onSuccess = (task: { name: ManagerProgressionTaskSlug }) => {
    if (!dailyActionTaskId) {
      return;
    }
    if (
      task.name === ManagerProgressionTaskSlug.DROP_CHALLENGE ||
      task.name === ManagerProgressionTaskSlug.DECISIVE_PLAYER_PICKER
    ) {
      // we don't use useLazyQuery here because it can be automatically re-triggered
      // with an archived daily action task id
      //https://github.com/apollographql/apollo-client/issues/9317
      apolloClient.query({
        query: REFRESH_DAILY_ACTION_TASK_QUERY,
        variables: { id: idFromObject(dailyActionTaskId) },
      });
    }
  };
  const { currentUser } = data;
  const { taskGroup } = currentUser || {};
  const decisivePlayerPickerStatId = taskGroup?.myTasks.find(
    task => task.name === ManagerProgressionTaskSlug.DECISIVE_PLAYER_PICKER
  )?.id;
  const dialogs = (
    <>
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => setPickerDialogTask(null)}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() =>
          setRevealPackCardsDialogReward(null)
        }
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => setRewardDialogTask(null)}
        onSuccess={onSuccess}
      />
      {!!showDecisivePlayerPickerDialog && !!decisivePlayerPickerStatId && (
        <DecisivePlayerPickerDialog
          taskId={decisivePlayerPickerStatId}
          onClose={() => setShowDecisivePlayerPickerDialog(false)}
          onSuccess={() => {
            setShowDecisivePlayerPickerDialog(false);
            onSuccess({
              name: ManagerProgressionTaskSlug.DECISIVE_PLAYER_PICKER,
            });
          }}
        />
      )}
    </>
  );

  if (['play', 'collect', 'connect'].includes(category)) {
    return (
      <>
        <MissionsPage
          sections={getTasksSections({
            taskGroup,
            isLaptop,
            sport,
            onDropChallengeClick: task => async () => setPickerDialogTask(task),
            onDecisivePlayerPickerClick: decisivePlayerPickerStatId
              ? () => setShowDecisivePlayerPickerDialog(true)
              : null,
            onClaim: task => async () => {
              await onClaim(task);
              onSuccess(task);
            },
            loadingTaskId,
          })}
        >
          {dialogs}
        </MissionsPage>
        {rarity === Rarity.common && <CommonMissionsTutorialDialog />}
      </>
    );
  }

  return <MissionsPage sections={[]} />;
};

export default MissionsCategoryRarityPage;
